import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  Divider,
  IconButton,
  Box,
  Dialog,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  RadioGroup,
  Radio,
  FormLabel,
  CardContent,
  Card,
  Tooltip,
  Snackbar,
  LinearProgress,
  DialogContent,
  DialogTitle,
  DialogActions,
  Modal,
  Backdrop,
  Fade,
  Badge,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DownloadIcon from "@mui/icons-material/Download";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import { format } from "date-fns";
import firebase from "firebase/app";
import { Autocomplete } from "@mui/material";
import "firebase/storage";
import "firebase/database";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: 1200,
  margin: "auto",
  backgroundColor: "#ffffff",
  borderRadius: 12,
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
}));

const TabPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  "&[hidden]": {
    display: "none",
  },
}));

// Enhanced document card with thumbnail support
const DocumentCard = styled(Card)(({ theme }) => ({
  position: "relative",
  marginBottom: theme.spacing(2),
  borderRadius: 8,
  overflow: "hidden",
  transition: "transform 0.2s, box-shadow 0.2s",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
  },
  "&:hover .document-actions": {
    opacity: 1,
  },
  height: 220,
  display: "flex",
  flexDirection: "column",
}));

const DocumentThumbnail = styled(Box)(({ theme }) => ({
  height: 140,
  overflow: "hidden",
  backgroundColor: "#f5f5f5",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
}));

const ThumbnailImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

const ThumbnailIcon = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  color: theme.palette.text.secondary,
  backgroundColor: "#f0f0f0",
}));

const DocumentInfo = styled(CardContent)(({ theme }) => ({
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: theme.spacing(1.5),
}));

const DocumentActions = styled(Box)({
  position: "absolute",
  top: 8,
  right: 8,
  opacity: 0,
  transition: "opacity 0.2s ease-in-out",
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  borderRadius: 4,
  padding: 4,
  zIndex: 10,
});

const ModalContent = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "#fff",
  borderRadius: 8,
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
  padding: theme.spacing(3),
  maxWidth: "90vw",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
}));

const ImageContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "auto",
  flexGrow: 1,
});

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "rgba(255, 255, 255, 0.8)",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
  },
  zIndex: 10,
}));

const ModalControlsTop = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  marginBottom: theme.spacing(2),
}));

const ModalControlsBottom = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  marginTop: theme.spacing(2),
}));

const ThumbnailsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  overflowX: "auto",
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  "&::-webkit-scrollbar": {
    height: 6,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#f0f0f0",
    borderRadius: 3,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#bdbdbd",
    borderRadius: 3,
    "&:hover": {
      backgroundColor: "#9e9e9e",
    },
  },
}));

const ThumbnailItem = styled(Box)(({ theme, active }) => ({
  width: 60,
  height: 60,
  borderRadius: 4,
  overflow: "hidden",
  cursor: "pointer",
  border: active
    ? `2px solid ${theme.palette.primary.main}`
    : "2px solid transparent",
  opacity: active ? 1 : 0.7,
  transition: "all 0.2s",
  "&:hover": {
    opacity: 1,
  },
}));

const MaintenanceCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  position: "relative",
  "&:hover": {
    boxShadow: theme.shadows[4],
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const LoadingOverlay = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(255, 255, 255, 0.7)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
});

const ErrorText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: "0.75rem",
  marginTop: theme.spacing(0.5),
}));

const ValidationMessage = ({ errors, name }) => {
  if (!errors[name]) return null;
  return <ErrorText>{errors[name]}</ErrorText>;
};

// Document Viewer Modal component
const DocumentViewerModal = ({
  open,
  onClose,
  documents,
  currentIndex,
  setCurrentIndex,
}) => {
  const [zoomLevel, setZoomLevel] = useState(1);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : documents.length - 1));
    setZoomLevel(1);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < documents.length - 1 ? prev + 1 : 0));
    setZoomLevel(1);
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleDownload = () => {
    if (documents[currentIndex]) {
      window.open(documents[currentIndex].url, "_blank");
    }
  };

  const isImage = (doc) => {
    return doc && doc.type && doc.type.startsWith("image/");
  };

  const isPdf = (doc) => {
    return (
      doc &&
      (doc.type === "application/pdf" ||
        doc.name.toLowerCase().endsWith(".pdf"))
    );
  };

  const currentDoc = documents[currentIndex];

  if (!open || !currentDoc) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: { backgroundColor: "rgba(0, 0, 0, 0.85)" },
      }}
    >
      <Fade in={open}>
        <ModalContent sx={{ width: isPdf(currentDoc) ? 900 : "auto" }}>
          <ModalControlsTop>
            <Typography variant="h6" component="div" sx={{ fontWeight: 500 }}>
              {currentDoc.name}
            </Typography>
            <Box>
              <IconButton
                onClick={handleDownload}
                size="small"
                sx={{ mr: 1 }}
                title="Download"
              >
                <DownloadIcon />
              </IconButton>
              {isImage(currentDoc) && (
                <>
                  <IconButton
                    onClick={handleZoomOut}
                    size="small"
                    sx={{ mr: 1 }}
                    title="Zoom out"
                  >
                    <ZoomOutIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleZoomIn}
                    size="small"
                    sx={{ mr: 1 }}
                    title="Zoom in"
                  >
                    <ZoomInIcon />
                  </IconButton>
                </>
              )}
              <IconButton onClick={onClose} size="small" title="Close">
                <CloseIcon />
              </IconButton>
            </Box>
          </ModalControlsTop>

          <ImageContainer>
            <NavigationButton
              onClick={handlePrevious}
              sx={{ left: 10 }}
              title="Previous document"
            >
              <ArrowBackIosNewIcon />
            </NavigationButton>

            {isImage(currentDoc) ? (
              <img
                src={currentDoc.url}
                alt={currentDoc.name}
                style={{
                  maxWidth: "100%",
                  maxHeight: "60vh",
                  transform: `scale(${zoomLevel})`,
                  transition: "transform 0.2s",
                }}
              />
            ) : isPdf(currentDoc) ? (
              <iframe
                src={`${currentDoc.url}#view=FitH`}
                width="100%"
                height="600px"
                title={currentDoc.name}
                style={{ border: "none" }}
              />
            ) : (
              <Box
                sx={{
                  p: 4,
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <InsertDriveFileIcon
                  sx={{ fontSize: 80, color: "text.secondary", mb: 2 }}
                />
                <Typography variant="body1">
                  This file type cannot be previewed.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  sx={{ mt: 2 }}
                >
                  Download File
                </Button>
              </Box>
            )}

            <NavigationButton
              onClick={handleNext}
              sx={{ right: 10 }}
              title="Next document"
            >
              <ArrowForwardIosIcon />
            </NavigationButton>
          </ImageContainer>

          <ModalControlsBottom>
            <Typography variant="body2" color="text.secondary">
              {currentIndex + 1} of {documents.length}
            </Typography>
          </ModalControlsBottom>

          <ThumbnailsContainer>
            {documents.map((doc, index) => (
              <ThumbnailItem
                key={index}
                active={index === currentIndex}
                onClick={() => {
                  setCurrentIndex(index);
                  setZoomLevel(1);
                }}
              >
                {isImage(doc) ? (
                  <img
                    src={doc.url}
                    alt={`thumbnail-${index}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : isPdf(doc) ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <PictureAsPdfIcon sx={{ color: "#e53935" }} />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <DescriptionIcon />
                  </Box>
                )}
              </ThumbnailItem>
            ))}
          </ThumbnailsContainer>
        </ModalContent>
      </Fade>
    </Modal>
  );
};

const VehicleForm = ({
  vehicle,
  onSubmit,
  projects,
  mode = "add",
  loading,
  onClose,
  firmId,
  vehicleTypes,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    readingType: "hrs",
    tag: "",
    vehicleNumber: "",
    vehicleType: "",
    location: "",
    make: "",
    model: "",
    chassisNumber: "",
    totalHoursWorked: "0",
    totalDieselConsumed: "0",
    engine2: {
      totalHoursWorked: "0",
      totalDieselConsumed: "0",
      chassisNumber: "",
    },
    yearOfPurchase: "",
    monthOfPurchase: "",
    invoiceStatus: false,
    documents: [],
    maintenanceSchedule: [],
    projectId: "",
    sos: false,
    transferred: false,
    transferredFromAnotherSite: false,
    totalKilometers: "0",
    projectName: "",
    insurances: [
      {
        startDate: null,
        endDate: null,
        provider: "",
      },
    ],
  });

  // Document viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentDocIndex, setCurrentDocIndex] = useState(0);

  const filterModal = React.useRef(null);
  const [engine2Enabled, setEngine2Enabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [documents, setDocuments] = useState([]);
  const [showImageOptions, setShowImageOptions] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedProject, setSelectedProject] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [maintenanceItems, setMaintenanceItems] = useState([]);
  const [currentMaintenanceItem, setCurrentMaintenanceItem] = useState({
    part: "",
    service: "",
    lastService: new Date(),
    cycle: "",
    cycleType: "days",
    serviceHistory: {},
  });

  const [editingMaintenanceIndex, setEditingMaintenanceIndex] = useState(null);
  const [showMaintenanceForm, setShowMaintenanceForm] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  // Helper function to determine if a document is an image
  const isImage = (doc) => {
    return doc && doc.type && doc.type.startsWith("image/");
  };

  // Helper function to determine if a document is a PDF
  const isPdf = (doc) => {
    return (
      doc &&
      (doc.type === "application/pdf" ||
        doc.name.toLowerCase().endsWith(".pdf"))
    );
  };

  // Helper function to safely convert date strings to Date objects
  const safelyParseDate = (dateValue) => {
    if (!dateValue) return null;

    // If it's already a Date object
    if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
      return dateValue;
    }

    // If it's a string, try to parse it
    if (typeof dateValue === "string") {
      try {
        const parsedDate = new Date(dateValue);
        return isNaN(parsedDate.getTime()) ? null : parsedDate;
      } catch (e) {
        console.error("Error parsing date:", e);
        return null;
      }
    }

    // If we can't handle it, return null
    return null;
  };

  useEffect(() => {
    if (selectedProject) {
      const project = projects.find(
        (project) => project.projectID === selectedProject
      );

      if (project) {
        setFormData((prev) => ({
          ...prev,
          location: project.projectAdress || "",
          projectName: project.projectName || "",
        }));
      }
    }
  }, [selectedProject, projects]);

  useEffect(() => {
    if (vehicle && mode === "edit") {
      // Process insurances to ensure proper date objects
      let processedInsurances = [];

      if (vehicle.insurances && Array.isArray(vehicle.insurances)) {
        processedInsurances = vehicle.insurances.map((insurance) => ({
          startDate: safelyParseDate(insurance.startDate),
          endDate: safelyParseDate(insurance.endDate),
          provider: insurance.provider || "",
        }));
      }

      // If no insurances, initialize with one empty entry
      if (processedInsurances.length === 0) {
        processedInsurances = [
          {
            startDate: null,
            endDate: null,
            provider: "",
          },
        ];
      }

      const vehicleData = {
        ...vehicle,
        // Process other dates as well
        insuranceStartDate: safelyParseDate(vehicle.insuranceStartDate),
        insuranceEndDate: safelyParseDate(vehicle.insuranceEndDate),
        permitValidUpto: safelyParseDate(vehicle.permitValidUpto),
        fcValidUpto: safelyParseDate(vehicle.fcValidUpto),
        roadTaxValidUpto: safelyParseDate(vehicle.roadTaxValidUpto),
        insurances: processedInsurances,
        projectId: vehicle.projectId || "",
        projectName: vehicle.projectName || "",
      };

      setFormData(vehicleData);
      setSelectedProject(vehicle.projectId);

      // Find and set the full project details
      const project = projects.find((p) => p.projectID === vehicle.projectId);

      if (project) {
        setFormData((prev) => ({
          ...prev,
          location: project.projectAdress || "",
        }));
      }

      // Set engine2Enabled if there's engine2 data
      if (vehicle.engine2) {
        setEngine2Enabled(true);
      }

      // Set documents if they exist
      if (vehicle.documents && vehicle.documents.length > 0) {
        setDocuments(vehicle.documents);
      }

      // Set maintenance items if they exist
      if (
        vehicle.maintenanceSchedule &&
        vehicle.maintenanceSchedule.length > 0
      ) {
        const processedMaintenanceItems = vehicle.maintenanceSchedule.map(
          (item) => ({
            ...item,
            lastService: safelyParseDate(item.lastService) || new Date(),
          })
        );
        setMaintenanceItems(processedMaintenanceItems);
      }
    }
  }, [vehicle, mode, projects]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Sanitize vehicle number and chassis number
    if (name === "vehicleNumber" || name === "chassisNumber") {
      // Remove special characters
      const sanitizedValue = value.replace(/[^a-zA-Z0-9]/g, "");

      setFormData((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));

    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleDateChange = (name, date) => {
    setFormData((prev) => ({
      ...prev,
      [name]: safelyParseDate(date),
    }));
  };

  const handleEngine2Change = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      engine2: {
        ...prev.engine2,
        [name]: value,
      },
    }));
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showNotification = (message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Modified insurance handlers with better date handling
  const handleInsuranceChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedInsurances = [...(prev.insurances || [])];
      if (!updatedInsurances[index]) {
        updatedInsurances[index] = {
          startDate: null,
          endDate: null,
          provider: "",
        };
      }

      // If changing a date field, ensure it's a valid date or null
      const processedValue =
        field === "startDate" || field === "endDate"
          ? safelyParseDate(value)
          : value;

      updatedInsurances[index] = {
        ...updatedInsurances[index],
        [field]: processedValue,
      };

      return {
        ...prev,
        insurances: updatedInsurances,
      };
    });
  };

  const handleAddInsurance = () => {
    setFormData((prev) => ({
      ...prev,
      insurances: [
        ...(prev.insurances || []),
        {
          startDate: null,
          endDate: null,
          provider: "",
        },
      ],
    }));
  };

  const handleRemoveInsurance = (index) => {
    setFormData((prev) => {
      const updatedInsurances = (prev.insurances || []).filter(
        (_, i) => i !== index
      );
      return {
        ...prev,
        insurances: updatedInsurances,
      };
    });

    showNotification("Insurance entry removed", "success");
  };

  const generateTag = (vehicleType, identifier) => {
    // Remove any special characters and spaces
    const sanitizedIdentifier = identifier.replace(/[^a-zA-Z0-9]/g, "");
    return (
      vehicleType.toUpperCase() + sanitizedIdentifier.slice(-4)
    ).toUpperCase();
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.vehicleNumber && !formData.chassisNumber) {
      newErrors.vehicleIdentification =
        "Either Vehicle Number or Chassis Number is required";
    }
    if (!formData.vehicleType) {
      newErrors.vehicleType = "Vehicle Type is required";
    }
    if (!formData.projectId) {
      newErrors.projectId = "Project is required";
    }

    if (formData.totalHoursWorked && isNaN(formData.totalHoursWorked)) {
      newErrors.totalHoursWorked = "Must be a number";
    }
    if (formData.totalDieselConsumed && isNaN(formData.totalDieselConsumed)) {
      newErrors.totalDieselConsumed = "Must be a number";
    }

    if (engine2Enabled) {
      if (
        formData.engine2.totalHoursWorked &&
        isNaN(formData.engine2.totalHoursWorked)
      ) {
        newErrors.engine2_totalHoursWorked = "Must be a number";
      }
      if (
        formData.engine2.totalDieselConsumed &&
        isNaN(formData.engine2.totalDieselConsumed)
      ) {
        newErrors.engine2_totalDieselConsumed = "Must be a number";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: "Please fix all errors before submitting",
        severity: "error",
      });
      return;
    }
    let tag =
      mode === "add"
        ? generateTag(
            formData.vehicleType,
            formData.vehicleNumber || formData.chassisNumber
          )
        : formData.tag;
    try {
      const submitData = {
        ...formData,
        engine2: engine2Enabled ? formData.engine2 : null,
        documents: documents,
        maintenanceSchedule: maintenanceItems,
        tag: tag,
      };

      console.log("Form data being submitted:", submitData);
      await onSubmit(submitData);
    } catch (error) {
      console.error("Error in form submission:", error);
      setErrors((prev) => ({ ...prev, submit: "Error submitting form" }));
      setSnackbar({
        open: true,
        message: "Error submitting form",
        severity: "error",
      });
    }
  };

  const handleFileUpload = async (files) => {
    
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const storageRef = firebase
          .storage()
          .ref(`vehicleDocuments/${firmId}`)
          .child(`${file.name}_${new Date().getTime()}`);

        const uploadTask = storageRef.put(file);

        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
          },
          (error) => {
            console.error("Upload error:", error);
            showNotification("Error uploading file", "error");
          },
          async () => {
            const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
            setDocuments((prev) => [
              ...prev,
              {
                name: file.name,
                type: file.type,
                url: downloadURL,
                timestamp: new Date().toISOString(),
              },
            ]);

            if (i === files.length - 1) {
              setUploading(false);
              setUploadProgress(0);
              showNotification("Files uploaded successfully", "success");
            }
          }
        );
      }
    } catch (error) {
      console.error("Error in file upload:", error);
      setUploading(false);
      setUploadProgress(0);
      showNotification("Error uploading files", "error");
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    handleFileUpload(files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const files = Array.from(event.dataTransfer.files);
    handleFileUpload(files);
  };

  const removeDocument = async (index, event) => {
    event.stopPropagation(); // Prevent opening the modal when deleting

    try {
      const doc = documents[index];
      if (doc.url) {
        const fileRef = firebase.storage().refFromURL(doc.url);
        await fileRef.delete();
      }

      setDocuments((prev) => prev.filter((_, i) => i !== index));
      showNotification("Document removed successfully", "success");
    } catch (error) {
      console.error("Error removing document:", error);
      showNotification("Error removing document", "error");
    }
  };

  const openDocumentViewer = (index) => {
    setCurrentDocIndex(index);
    setViewerOpen(true);
  };

  const handleMaintenanceSubmit = (e) => {
    e.preventDefault();
    if (!currentMaintenanceItem.part || !currentMaintenanceItem.service) {
      showNotification("Please fill all maintenance fields", "error");
      return;
    }

    if (editingMaintenanceIndex !== null) {
      setMaintenanceItems((prev) =>
        prev.map((item, index) =>
          index === editingMaintenanceIndex ? currentMaintenanceItem : item
        )
      );
    } else {
      setMaintenanceItems((prev) => [...prev, currentMaintenanceItem]);
    }

    setCurrentMaintenanceItem({
      part: "",
      service: "",
      lastService: new Date(),
      cycle: "",
      cycleType: "days",
      serviceHistory: {},
    });
    setEditingMaintenanceIndex(null);
    setShowMaintenanceForm(false);
    showNotification("Maintenance schedule updated", "success");
  };

  const removeMaintenance = (index) => {
    setMaintenanceItems((prev) => prev.filter((_, i) => i !== index));
    showNotification("Maintenance item removed", "success");
  };

  const handleProjectChange = (event, newValue) => {
    // When using Autocomplete
    if (typeof newValue === "object" && newValue !== null) {
      setFormData((prev) => ({
        ...prev,
        projectId: newValue.projectID,
        projectName: newValue.projectName,
        location: newValue.projectAdress || "",
      }));
      setSelectedProject(newValue.projectID);
    }
    // When using Select
    else {
      const selectedProject = projects.find(
        (project) => project.projectID === event.target.value
      );

      if (selectedProject) {
        setFormData((prev) => ({
          ...prev,
          projectId: selectedProject.projectID,
          projectName: selectedProject.projectName,
          location: selectedProject.projectAdress || "",
        }));
        setSelectedProject(selectedProject.projectID);
      }
    }
  };

  const editMaintenance = (index) => {
    console.log("Editing maintenance item:", maintenanceItems[index]);
    setCurrentMaintenanceItem(maintenanceItems[index]);
    setEditingMaintenanceIndex(index);
    setShowMaintenanceForm(true);
  };

  const renderBasicInformation = () => (
    <TabPanel value={activeTab} index={0}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={projects}
            getOptionLabel={(option) => option.projectName || ""}
            value={
              projects.find(
                (project) => project.projectID === formData.projectId
              ) || null
            }
            onChange={(event, newValue) => {
              // When using Autocomplete
              if (typeof newValue === "object" && newValue !== null) {
                setFormData((prev) => ({
                  ...prev,
                  projectId: newValue.projectID,
                  projectName: newValue.projectName,
                  location: newValue.projectAdress || "",
                }));
                setSelectedProject(newValue.projectID);
              } else {
                // Handle clearing the selection
                setFormData((prev) => ({
                  ...prev,
                  projectId: "",
                  projectName: "",
                  location: "",
                }));
                setSelectedProject("");
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Project"
                error={!!errors.projectId}
                helperText={errors.projectId}
                fullWidth
              />
            )}
            disabled={mode === "edit"}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Vehicle Number"
            name="vehicleNumber"
            value={formData.vehicleNumber}
            onChange={handleChange}
            error={!!errors.vehicleNumber}
            helperText={errors.vehicleNumber}
            inputProps={{
              pattern: "[a-zA-Z0-9]*",
              title: "Only alphanumeric characters are allowed",
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Autocomplete
            freeSolo
            disabled={mode === "edit"}
            options={vehicleTypes || []}
            value={formData.vehicleType}
            onChange={(event, newValue) => {
              handleChange({
                target: {
                  name: "vehicleType",
                  value: newValue || "",
                },
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Vehicle Type"
                name="vehicleType"
                error={!!errors.vehicleType}
                helperText={errors.vehicleType}
                fullWidth
              />
            )}
            ListboxProps={{
              style: { maxHeight: 200 },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Chassis Number"
            name="chassisNumber"
            value={formData.chassisNumber}
            onChange={handleChange}
            error={!!errors.chassisNumber}
            helperText={errors.chassisNumber}
            inputProps={{
              pattern: "[a-zA-Z0-9]*",
              title: "Only alphanumeric characters are allowed",
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Make"
            name="make"
            value={formData.make}
            onChange={handleChange}
            error={!!errors.make}
            helperText={errors.make}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Model"
            name="model"
            value={formData.model}
            onChange={handleChange}
            error={!!errors.model}
            helperText={errors.model}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            disabled={true}
            label="Location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            multiline
            rows={2}
          />
        </Grid>
      </Grid>
    </TabPanel>
  );

  const renderEngineDetails = () => (
    <TabPanel value={activeTab} index={1}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormLabel component="legend">Reading Type</FormLabel>
            <RadioGroup
              row
              name="readingType"
              value={formData.readingType}
              onChange={(e) =>
                handleChange({
                  target: {
                    name: "readingType",
                    value: e.target.value,
                  },
              })
              }
            >
              <FormControlLabel value="hrs" control={<Radio />} label="Hours" />
              <FormControlLabel
                value="km"
                control={<Radio />}
                label="Kilometers"
              />
              <FormControlLabel value="kmhr" control={<Radio />} label="Both" />
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom color="primary">
            Engine 1
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Total Hours Worked"
            name="totalHoursWorked"
            type="number"
            value={formData.totalHoursWorked}
            onChange={handleChange}
            error={!!errors.totalHoursWorked}
            helperText={errors.totalHoursWorked}
          />
        </Grid>
        {formData.readingType === "km" && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Total Kilometers Travelled"
              name="totalKilometers"
              type="number"
              value={formData.totalKilometers}
              onChange={handleChange}
              error={!!errors.totalKilometers}
              helperText={errors.totalKilometers}
            />
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Total Diesel Consumed (L)"
            name="totalDieselConsumed"
            type="number"
            value={formData.totalDieselConsumed}
            onChange={handleChange}
            error={!!errors.totalDieselConsumed}
            helperText={errors.totalDieselConsumed}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={
              <Switch
                checked={engine2Enabled}
                onChange={(e) => setEngine2Enabled(e.target.checked)}
              />
            }
            label="Enable Engine 2"
          />
        </Grid>

        {engine2Enabled && (
          <>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                Engine 2
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Chassis Number"
                name="chassisNumber"
                value={formData.engine2.chassisNumber}
                onChange={handleEngine2Change}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={
                  formData.readingType === "hrs"
                    ? "Total Hours Worked"
                    : "Total Kilometers Travelled"
                }
                name="totalHoursWorked"
                type="number"
                value={formData.engine2.totalHoursWorked}
                onChange={handleEngine2Change}
                error={!!errors.engine2_totalHoursWorked}
                helperText={errors.engine2_totalHoursWorked}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Total Diesel Consumed (L)"
                name="totalDieselConsumed"
                type="number"
                value={formData.engine2.totalDieselConsumed}
                onChange={handleEngine2Change}
                error={!!errors.engine2_totalDieselConsumed}
                helperText={errors.engine2_totalDieselConsumed}
              />
            </Grid>
          </>
        )}
      </Grid>
    </TabPanel>
  );

  const renderDocumentation = () => (
    <TabPanel value={activeTab} index={2}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom color="primary">
            Purchase Information
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Year of Purchase"
            name="yearOfPurchase"
            value={formData.yearOfPurchase}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Month of Purchase"
            name="monthOfPurchase"
            value={formData.monthOfPurchase}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.invoiceStatus}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "invoiceStatus",
                      value: e.target.checked,
                    },
                  })
                }
              />
            }
            label="Invoice Status"
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom color="primary">
            Insurance Details
          </Typography>
        </Grid>

        {/* Multiple Insurance Entries */}
        {(formData.insurances || []).map((insurance, index) => (
          <React.Fragment key={index}>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight="medium">
                  Insurance {index + 1}
                </Typography>
                {index > 0 && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveInsurance(index)}
                    sx={{ ml: 1 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Insurance Start Date"
                  value={insurance.startDate}
                  onChange={(date) =>
                    handleInsuranceChange(index, "startDate", date)
                  }
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Insurance End Date"
                  value={insurance.endDate}
                  onChange={(date) =>
                    handleInsuranceChange(index, "endDate", date)
                  }
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Insurance Provider"
                name="provider"
                value={insurance.provider || ""}
                onChange={(e) =>
                  handleInsuranceChange(index, "provider", e.target.value)
                }
              />
            </Grid>
          </React.Fragment>
        ))}

        <Grid item xs={12}>
          <Button
            variant="outlined"
            startIcon={<AddPhotoAlternateIcon />}
            onClick={handleAddInsurance}
            sx={{ mt: 1 }}
          >
            Add Another Insurance
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom color="primary">
            Permit and Fitness Details
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Permit Number"
            name="permitNo"
            value={formData.permitNo}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Permit Range"
            name="permitRange"
            value={formData.permitRange}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Permit Valid Upto"
              value={formData.permitValidUpto}
              onChange={(date) => handleDateChange("permitValidUpto", date)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Fitness Certificate Number"
            name="fitnessCertificateNo"
            value={formData.fitnessCertificateNo}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="FC Valid Upto"
              value={formData.fcValidUpto}
              onChange={(date) => handleDateChange("fcValidUpto", date)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Road Tax Valid Upto"
              value={formData.roadTaxValidUpto}
              onChange={(date) => handleDateChange("roadTaxValidUpto", date)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12}>
          <Box
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            sx={{
              border: "2px dashed #ccc",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              backgroundColor: "#fafafa",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "#f0f0f0",
              },
            }}
          >
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: "none" }}
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <CloudUploadIcon sx={{ fontSize: 48, color: "primary.main" }} />
              <Typography variant="h6" gutterBottom>
                Drag and drop files here or click to upload
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Supported formats: PDF, JPG, PNG
              </Typography>
            </label>
          </Box>
        </Grid>

        {uploading && (
          <Grid item xs={12}>
            <Box sx={{ width: "100%", mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" color="textSecondary" align="center">
                Uploading: {Math.round(uploadProgress)}%
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Enhanced document gallery */}
        <Grid item xs={12}>
          <Typography variant="h6" color="primary" gutterBottom>
            Uploaded Documents ({documents.length})
          </Typography>
          <Grid container spacing={2}>
            {documents.map((doc, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <DocumentCard onClick={() => openDocumentViewer(index)}>
                  <DocumentThumbnail>
                    {isImage(doc) ? (
                      <ThumbnailImage src={doc.url} alt={doc.name} />
                    ) : isPdf(doc) ? (
                      <ThumbnailIcon>
                        <PictureAsPdfIcon
                          sx={{ fontSize: 64, color: "#e53935" }}
                        />
                      </ThumbnailIcon>
                    ) : (
                      <ThumbnailIcon>
                        <InsertDriveFileIcon sx={{ fontSize: 64 }} />
                      </ThumbnailIcon>
                    )}
                    <DocumentActions className="document-actions">
                      <IconButton
                        size="small"
                        onClick={(e) => removeDocument(index, e)}
                        sx={{ bgcolor: "rgba(255, 255, 255, 0.9)" }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </DocumentActions>
                  </DocumentThumbnail>

                  <DocumentInfo>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        noWrap
                        title={doc.name}
                        sx={{ fontWeight: 500 }}
                      >
                        {doc.name}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      color="primary"
                      component="div"
                      sx={{ mt: 1, cursor: "pointer" }}
                    >
                      Click to view
                    </Typography>
                  </DocumentInfo>
                </DocumentCard>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Document Viewer Modal */}
        <DocumentViewerModal
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          documents={documents}
          currentIndex={currentDocIndex}
          setCurrentIndex={setCurrentDocIndex}
        />
      </Grid>
    </TabPanel>
  );

  const renderMaintenance = () => (
    <TabPanel value={activeTab} index={3}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddPhotoAlternateIcon />}
            onClick={() => setShowMaintenanceForm(true)}
            sx={{ mb: 3 }}
          >
            Add Maintenance Schedule
          </Button>
        </Grid>

        {showMaintenanceForm && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {editingMaintenanceIndex !== null
                  ? "Edit Maintenance Schedule"
                  : "New Maintenance Schedule"}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Part"
                    value={currentMaintenanceItem.part}
                    onChange={(e) =>
                      setCurrentMaintenanceItem((prev) => ({
                        ...prev,
                        part: e.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Service"
                    value={currentMaintenanceItem.service}
                    onChange={(e) =>
                      setCurrentMaintenanceItem((prev) => ({
                        ...prev,
                        service: e.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Last Service Date"
                      value={
                        safelyParseDate(currentMaintenanceItem.lastService) ||
                        new Date()
                      }
                      onChange={(date) =>
                        setCurrentMaintenanceItem((prev) => ({
                          ...prev,
                          lastService: safelyParseDate(date) || new Date(),
                        }))
                      }
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Cycle"
                    type="number"
                    value={currentMaintenanceItem.cycle}
                    onChange={(e) =>
                      setCurrentMaintenanceItem((prev) => ({
                        ...prev,
                        cycle: e.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Cycle Type</FormLabel>
                    <RadioGroup
                      row
                      value={currentMaintenanceItem.cycleType}
                      onChange={(e) =>
                        setCurrentMaintenanceItem((prev) => ({
                          ...prev,
                          cycleType: e.target.value,
                        }))
                      }
                    >
                      <FormControlLabel
                        value="days"
                        control={<Radio />}
                        label="Days"
                      />
                      <FormControlLabel
                        value="km"
                        control={<Radio />}
                        label="Kilometers"
                      />
                      <FormControlLabel
                        value="hours"
                        control={<Radio />}
                        label="Hours"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setShowMaintenanceForm(false);
                        setEditingMaintenanceIndex(null);
                        setCurrentMaintenanceItem({
                          part: "",
                          service: "",
                          lastService: new Date(),
                          cycle: "",
                          cycleType: "days",
                          serviceHistory: {},
                        });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleMaintenanceSubmit}
                    >
                      {editingMaintenanceIndex !== null ? "Update" : "Add"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        {maintenanceItems.map((item, index) => (
          <Grid item xs={12} key={index}>
            <MaintenanceCard>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {item.part}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="textSecondary"
                      gutterBottom
                    >
                      Service: {item.service}
                    </Typography>
                    <Typography variant="body2">
                      Last Service:{" "}
                      {item.lastService instanceof Date &&
                      !isNaN(item.lastService.getTime())
                        ? format(item.lastService, "dd/MM/yyyy")
                        : "N/A"}
                    </Typography>
                    <Typography variant="body2">
                      Cycle: {item.cycle} {item.cycleType}
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => editMaintenance(index)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => removeMaintenance(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </MaintenanceCard>
          </Grid>
        ))}
      </Grid>
    </TabPanel>
  );

  return (
    <StyledPaper>
      {loading && (
        <LoadingOverlay>
          <CircularProgress />
        </LoadingOverlay>
      )}

      <form onSubmit={handleFormSubmit}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Basic Information" />
            {/* <Tab label="Engine Details" />
            <Tab label="Documentation" />
            <Tab label="Maintenance" /> */}
          </Tabs>
        </Box>

        {renderBasicInformation()}
        {renderEngineDetails()}
        {renderDocumentation()}
        {renderMaintenance()}

        <Box
          sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
        >
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{ minWidth: 150 }}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : mode === "add" ? (
              "Add Vehicle"
            ) : (
              "Update Vehicle"
            )}
          </Button>
        </Box>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        severity={snackbar.severity}
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={handleSnackbarClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </StyledPaper>
  );
};

export default VehicleForm;
